import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language, Labels } from '../types';
import { enLabels } from '../labels/en';
import { daLabels } from '../labels/da';
import { useAuth } from './AuthContext';

interface LanguageContextType {
  language: Language;
  labels: Labels;
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const getLabelsForLanguage = (language: Language): Labels => {
  switch (language) {
    case 'en':
      return enLabels;
    case 'da':
      return daLabels;
    default:
      return daLabels; // Default to Danish
  }
};

const getUserLanguage = (user: any): Language => {
  if (user?.user_metadata?.language) {
    const userLang = user.user_metadata.language;
    if (userLang === 'en' || userLang === 'da') {
      return userLang as Language;
    }
  }
  return 'da'; // Default to Danish
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [language, setLanguageState] = useState<Language>('da');

  // Update language when user changes or on auth state change
  useEffect(() => {
    // Only update language after auth loading is complete
    if (!isLoading) {
      if (isAuthenticated && user) {
        const userLanguage = getUserLanguage(user);
        setLanguageState(userLanguage);
      } else {
        // For non-authenticated users, default to Danish
        setLanguageState('da');
      }
    }
  }, [user, isAuthenticated, isLoading]);

  const setLanguage = async (newLanguage: Language) => {
    setLanguageState(newLanguage);
  };

  const labels = getLabelsForLanguage(language);

  return (
    <LanguageContext.Provider value={{ language, labels, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}; 